import { useState } from "react";
import { toast } from "sonner";
import { Search, UserPlus, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Field, inputCls, primaryBtnCls, ghostBtnCls } from "@/components/Kit";
import { PatientForm, PATIENT_EMPTY, validatePatientForm, patientPayload } from "@/components/PatientForm";
import { firstError } from "@/lib/validate";
import { dmy } from "@/lib/format";

export { ID_TYPES } from "@/lib/validate";

/** Patient chooser reused by OPD registration and IPD admission. */
export function PatientPicker({ selected, onSelect }) {
  const [mode, setMode] = useState("existing");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [form, setForm] = useState(PATIENT_EMPTY);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);

  const search = async (value) => {
    setQ(value);
    if (value.trim().length < 2) return setResults([]);
    try {
      const { data } = await api.get("/hospital/patients", { params: { q: value.trim(), limit: 8 } });
      setResults(data.items);
    } catch { setResults([]); }
  };

  const register = async () => {
    const e = validatePatientForm(form);
    setErrors(e);
    if (Object.keys(e).length) { setFormError(firstError(e)); return; }
    setBusy(true); setFormError("");
    try {
      const { data } = await api.post("/hospital/patients", patientPayload(form));
      toast.success(`Patient registered — UHID ${data.patient.uhid}`);
      onSelect(data.patient);
      setForm(PATIENT_EMPTY);
    } catch (err) { setFormError(apiError(err)); } finally { setBusy(false); }
  };

  if (selected) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="picker-selected">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="flex items-center gap-2 font-semibold text-slate-900">
              <Check className="h-4 w-4 text-emerald-700" /> {selected.name}
            </p>
            <p className="text-xs text-slate-700 mt-1">
              UHID {selected.uhid} · {selected.gender}{selected.age ? ` · ${selected.age} yrs` : ""} · {selected.phone}
              {selected.blood_group ? ` · ${selected.blood_group}` : ""}
            </p>
            {selected.city && <p className="text-xs text-slate-600 mt-0.5">{[selected.area, selected.city, selected.state].filter(Boolean).join(" · ")}</p>}
            <p className="text-xs text-slate-500 mt-0.5">Registered {dmy(selected.created_at)}</p>
          </div>
          <button type="button" onClick={() => onSelect(null)} className={ghostBtnCls} data-testid="picker-change-btn">Change</button>
        </div>
      </div>
    );
  }

  const tabCls = (on) => `inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium border ${
    on ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 text-slate-700 hover:border-slate-900"}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={() => setMode("existing")} className={tabCls(mode === "existing")} data-testid="picker-existing-btn">
          <Search className="h-4 w-4" /> Existing / follow-up patient
        </button>
        <button type="button" onClick={() => setMode("new")} className={tabCls(mode === "new")} data-testid="picker-new-btn">
          <UserPlus className="h-4 w-4" /> New patient
        </button>
      </div>

      {mode === "existing" ? (
        <div>
          <Field label="Search by mobile, name or UHID">
            <input className={inputCls} data-testid="picker-search-input" value={q}
              onChange={(e) => search(e.target.value)} placeholder="9876543210 / Ramesh / UH00012" />
          </Field>
          <div className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200">
            {results.length === 0 ? (
              <p className="p-4 text-sm text-slate-500">Type at least 2 characters to search your patient records.</p>
            ) : results.map((p) => (
              <button type="button" key={p.patient_id} onClick={() => onSelect(p)}
                data-testid={`picker-result-${p.patient_id}`}
                className="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-slate-50">
                <span>
                  <span className="block text-sm font-semibold text-slate-900">{p.name}</span>
                  <span className="block text-xs text-slate-600">UHID {p.uhid} · {p.phone} · {p.gender}</span>
                </span>
                <span className="text-xs font-medium text-[#EA580C]">Select</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-5" data-testid="picker-new-form">
          <PatientForm form={form} setForm={setForm} errors={errors} prefix="picker" />
          {formError && <p className="mt-5 text-sm font-medium text-red-600" data-testid="picker-form-error">{formError}</p>}
          <button type="button" onClick={register} disabled={busy} className={`mt-5 ${primaryBtnCls}`}
            data-testid="picker-register-btn">Register & continue</button>
          <p className="mt-2 text-xs text-slate-600">
            Government ID is treated as sensitive data and is visible only to authorised staff.
          </p>
        </div>
      )}
    </div>
  );
}
